import {DatePicker, Form, Select, Steps, Switch} from 'antd';
import styles from './popupAnnouncement.module.scss';
import {fieldRules, getUserTimezone} from 'utils/helper';
import {supportedLanguages} from 'utils/constants';
import {announcementType} from 'utils/mock';
import {Button} from '@dabafinance/react-components';
import {StepTwoAnnouncement} from './stepTwoAnnouncement';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {usePopupAnnouncementStore} from '../../../zustand/store';
import {StepThreePopupAnnouncement} from './stepThreePopupAnnouncement';
import {StepFourPopupAnnouncement} from './stepFourPopupAnnouncement';
import {
  AnnouncementEntitySelector,
  isSingleEntityType,
} from '../components/AnnouncementEntitySelector';
import {usePopupAnnouncement} from './usePopupAnnouncement';
import {GET_ALL_REPORT_ID} from 'graphql/queries/report';
import {GET_MINIMAL_CAMPAIGNS} from 'graphql/queries/campaign';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useEffect, useRef} from 'react';
import moment from 'moment';

export const PopupAnnouncement = () => {
  const popup = usePopupAnnouncement();
  const {form} = popup;
  const {popupAnnouncement, setPopupAnnouncement, setStep, step} =
    usePopupAnnouncementStore();
  const type = Form.useWatch('type', form);
  // SINGLE_OPPORTUNITY serves IPO, private equity and venture fund, so the
  // picker needs to know which of the three before it can list anything.
  // Kept on the form rather than in local state so a step change preserves it.
  const opportunityKind = Form.useWatch('opportunityKind', form);
  const setOpportunityKind = value =>
    form.setFieldsValue({opportunityKind: value});

  const {Option} = Select;

  const {data: getAllReports, loading: loadingReports} = useQuery(
    GET_ALL_REPORT_ID,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data: getAllCampaigns, loading: loadingCampaigns} = useQuery(
    GET_MINIMAL_CAMPAIGNS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const items = [
    {title: 'Settings'},
    {title: 'Target Audience'},
    {title: 'Popup Content'},
    {title: 'Finish'},
  ];

  // Hydrate form from store once
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (!hasHydratedRef.current && popupAnnouncement) {
      const {
        expiredDate,
        publishedDate,
        signupStartDate,
        signupEndDate,
        lastAppVisitedStartDate,
        lastAppVisitedEndDate,
        investmentStartDate,
        investmentEndDate,
        ...rest
      } = popupAnnouncement;
      form.setFieldsValue({
        ...rest,
        expiredDate: expiredDate ? moment(expiredDate) : null,
        publishedDate: publishedDate ? moment(publishedDate) : null,
        signupStartDate: signupStartDate ? moment(signupStartDate) : null,
        signupEndDate: signupEndDate ? moment(signupEndDate) : null,
        lastAppVisitedStartDate: lastAppVisitedStartDate
          ? moment(lastAppVisitedStartDate)
          : null,
        lastAppVisitedEndDate: lastAppVisitedEndDate
          ? moment(lastAppVisitedEndDate)
          : null,
        investmentStartDate: investmentStartDate
          ? moment(investmentStartDate)
          : null,
        investmentEndDate: investmentEndDate ? moment(investmentEndDate) : null,
      });
      hasHydratedRef.current = true;
    }
  }, [form, popupAnnouncement]);

  const handleStepSubmit = values => {
    if (step === 3) return;
    if (step === 2) {
      setPopupAnnouncement({
        ...popupAnnouncement,
        announcements: form.getFieldValue('announcements'),
      });
      setStep(step + 1);
    } else {
      setPopupAnnouncement({...popupAnnouncement, ...values});
      setStep(step + 1);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Create Popup Announcement</h1>
        <Steps current={step} items={items} className={styles.steps} />
        <Form
          onValuesChange={(_, allValues) =>
            setPopupAnnouncement({...popupAnnouncement, ...allValues})
          }
          onFinish={handleStepSubmit}
          form={form}
          layout="vertical">
          {step === 0 && (
            <>
              <Form.Item
                rules={[fieldRules('Language')]}
                label="Language"
                className={styles.fieldWidth}
                name="language">
                <Select>
                  {supportedLanguages.map(data => (
                    <Option key={data.value}>{data.label}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Announcement Type"
                className={styles.fieldWidth}
                name="type"
                rules={[fieldRules('Announcement Type')]}>
                <Select showSearch>
                  {announcementType.map(({key, name}) => (
                    <Option key={key} value={key}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {type === 'SINGLE_REPORT' && (
                <Form.Item
                  rules={[fieldRules('Report')]}
                  name="report"
                  className={styles.fieldWidth}
                  label="Report">
                  <Select
                    loading={loadingReports}
                    allowClear
                    placeholder="Select a report">
                    {getAllReports?.getAllReports?.map(({id, title}) => (
                      <Option key={id} value={id}>
                        {title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {type === 'SINGLE_OPPORTUNITY' && (
                <Form.Item
                  rules={[fieldRules('Opportunity')]}
                  name="opportunity"
                  className={styles.fieldWidth}
                  label="Opportunity">
                  <Select
                    loading={loadingCampaigns}
                    allowClear
                    placeholder="Select an opportunity">
                    {getAllCampaigns?.getAllCampaigns?.campaigns?.map(
                      campaign => (
                        <Option key={campaign.id} value={campaign.id}>
                          {campaign.fundraisingCompany?.companyName}
                        </Option>
                      ),
                    )}
                  </Select>
                </Form.Item>
              )}

              {isSingleEntityType(type) && (
                <AnnouncementEntitySelector
                  type={type}
                  opportunityKind={opportunityKind}
                  onOpportunityKindChange={setOpportunityKind}
                />
              )}

              {(type === 'EVENT' ||
                type === 'GENERAL' ||
                type === 'AUDIO' ||
                type === 'SIMULATOR') && (
                <Form.Item
                  label="Open url on click?"
                  className={styles.fieldWidth}
                  name="openUrlOnClick">
                  <Switch />
                </Form.Item>
              )}

              <div className={styles.dateFields}>
                <Form.Item
                  label="Published Date (Time is saved in UTC/GMT)"
                  name="publishedDate"
                  className={styles.fieldWidth}
                  rules={[fieldRules('Published Date')]}>
                  <DatePicker
                    renderExtraFooter={() => (
                      <div style={{textAlign: 'center'}}>
                        <p>Your timezone is: {getUserTimezone()}</p>
                      </div>
                    )}
                    format={'YYYY-MM-DD HH:mm:ss'}
                    showTime
                  />
                </Form.Item>
                <Form.Item
                  rules={[fieldRules('Expiry Date')]}
                  name="expiredDate"
                  className={styles.fieldWidth}
                  label="Expiration Date (Time is saved in UTC/GMT)">
                  <DatePicker
                    format={'YYYY-MM-DD HH:mm:ss'}
                    renderExtraFooter={() => (
                      <div style={{textAlign: 'center'}}>
                        <p>Your timezone is: {getUserTimezone()}</p>
                      </div>
                    )}
                    showTime
                  />
                </Form.Item>
              </div>
            </>
          )}
          {step === 1 && <StepTwoAnnouncement form={form} />}
          {step === 2 && (
            <StepThreePopupAnnouncement
              popupAnnouncement={popupAnnouncement}
              form={form}
              popup={popup}
            />
          )}
          {step === 3 && <StepFourPopupAnnouncement form={form} />}
          {step < 3 && (
            <div>
              {step > 0 && (
                <Button label="Back" onClick={() => setStep(step - 1)} />
              )}
              <SubmitButton label="Proceed to Next Step" smallWidth />
            </div>
          )}
        </Form>
      </div>
    </div>
  );
};
